<template>
  <div class="row hs-sections-page">
    <div class="col-md-12">
      <div class="card">
        <div :class="(loading ? 'show-loader' : 'hide-loader')">
          <div class="el-loading-spinner">
            <svg viewBox="25 25 50 50" class="circular">
              <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
            </svg>
          </div>
        </div>
        <div class="card-content row">
          <!-- Top bar -->
          <div class="col-sm-12 mb-10">
            <div class="pull-right">
              <button class="btn btn-info mar-bt-20 mar-rt-10" :disabled="exporting" @click="exportNewsletter">
                <i class="fa fa-download"></i>
                Exportă newsletter
              </button>
              <button class="btn add mar-bt-20" @click="openCreate">
                <img src="/static/img/icons/add.png">
                {{ $t('general.add') }}
              </button>
            </div>
          </div>

          <!-- Sections table -->
          <div class="col-sm-12">
            <div class="hs-table-wrap">
            <table class="table width-table" ref="sortableTable">
              <thead>
                <tr>
                  <th width="5%"></th>
                  <th width="20%">{{ $t('marketingOffers.title') }}</th>
                  <th width="20%">{{ typeColumnLabel }}</th>
                  <th width="20%">{{ contentColumnLabel }}</th>
                  <th width="10%">{{ $t('marketingOffers.status') }}</th>
                  <th width="10%">{{ $t('marketingOffers.actions') }}</th>
                  <th width="15%"></th>
                </tr>
              </thead>
              <draggable v-model="sections" :element="'tbody'" @end="onReorder">
                <template v-for="(item, index) in sections">
                  <tr class="sortableRow" :key="item.id">
                    <td>
                      <i style="cursor: move; color: #7C8DB5;" class="fa fa-arrows sortHandle"></i>
                    </td>
                    <td>
                      <div class="hs-title-cell" :title="item.title">{{ item.title }}</div>
                    </td>
                    <td>{{ typeLabel(item.section_type) }}</td>
                    <td>{{ contentSummary(item) }}</td>
                    <td>
                      <p-switch v-model="item.active" type="success" @input="handleActive(item)"></p-switch>
                    </td>
                    <td>
                      <a class="btn btn-danger btn-icon delete mar-rt-10" @click="handleDelete(index, item, $event)" :title="$t('general.delete')">
                        <img src="/static/img/icons/icon-close.png">
                        {{ $t('general.delete') }}
                      </a>
                    </td>
                    <td class="edit-icon-pointer" @click="openEdit(item)">
                      <div class="edit">
                        <i class="fa fa-pencil"></i>{{ $t('general.edit') }}
                      </div>
                    </td>
                  </tr>
                </template>
              </draggable>
            </table>
            </div>
            <p v-if="!loading && sections.length === 0" class="text-center mar-tp-20">
              Nicio secțiune adăugată încă.
            </p>
          </div>

          <!-- Add / Edit modal -->
          <vmodal name="sectionModal"
                  class="sectionModal"
                  width="95%"
                  :maxWidth="1300"
                  :minWidth="300"
                  height="auto"
                  :scrollable="true"
                  :adaptive="true"
                  @opened="onModalOpened">
            <div class="header border-bottom">
              <h4>{{ form.id ? $t('general.edit') : $t('general.add') }} — {{ typeLabel(form.section_type) }}</h4>
            </div>
            <div class="content-group">
              <div class="content_scrool">

                <!-- Section type (only on create) -->
                <div class="form-group" v-if="!form.id">
                  <label>{{ typeColumnLabel }}</label>
                  <select class="form-control" v-model.number="form.section_type">
                    <option v-for="t in availableTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>

                <!-- Title (all types) -->
                <div class="form-group">
                  <label>{{ $t('marketingOffers.title') }} <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="form.title" :maxlength="TITLE_MAX" />
                  <small class="text-muted pull-right">{{ (form.title || '').length }} / {{ TITLE_MAX }}</small>
                </div>

                <!-- Description (all types, optional) -->
                <div class="form-group">
                  <label>Descriere <small class="text-muted">(opțional)</small></label>
                  <wysiwyg v-model="form.description" />
                </div>

                <!-- TYPE 1: Images -->
                <div class="form-group" v-if="form.section_type === 1">
                  <label>{{ typeLabel(1) }} (minim {{ MIN_IMAGES }}, maxim {{ MAX_ITEMS }}) <span class="text-danger">*</span></label>
                  <el-upload
                    action=""
                    multiple
                    list-type="picture-card"
                    accept=".jpg,.jpeg,.png"
                    :file-list="form.imagelist"
                    :limit="MAX_ITEMS"
                    :auto-upload="false"
                    :on-change="handleImagesChange"
                    :on-remove="handleImagesRemove"
                    :on-exceed="handleExceed">
                    <img src="/static/img/icons/icon-plus-primary.png">
                  </el-upload>
                </div>

                <!-- TYPE 2: Hero (images + display mode) -->
                <div class="form-group" v-if="form.section_type === 2">
                  <label>Poze (maxim {{ MAX_ITEMS }})</label>
                  <el-upload
                    action=""
                    multiple
                    list-type="picture-card"
                    accept=".jpg,.jpeg,.png"
                    :file-list="form.imagelist"
                    :limit="MAX_ITEMS"
                    :auto-upload="false"
                    :on-change="handleImagesChange"
                    :on-remove="handleImagesRemove"
                    :on-exceed="handleExceed">
                    <img src="/static/img/icons/icon-plus-primary.png">
                  </el-upload>
                </div>
                <div class="form-group" v-if="form.section_type === 2">
                  <label>Cum arată pozele</label>
                  <select class="form-control" v-model="form.image_fit">
                    <option value="cover">Cover — umple tot spațiul (poate tăia din poză)</option>
                    <option value="contain">Contain — încape toată poza</option>
                  </select>
                </div>

                <!-- TYPE 3: Products -->
                <div class="form-group" v-if="form.section_type === 3">
                  <label>{{ typeLabel(3) }} (max {{ MAX_ITEMS }})</label>
                  <el-select
                    v-model="form.selectedProducts"
                    class="hs-product-select"
                    multiple
                    filterable
                    remote
                    reserve-keyword
                    popper-class="hs-product-dropdown"
                    :remote-method="searchProducts"
                    :loading="productLoading"
                    :multiple-limit="MAX_ITEMS"
                    style="width: 100%;"
                    @visible-change="onProductDropdownVisible"
                    :placeholder="$t('marketingOffers.pickProd')">
                    <el-option
                      v-for="p in products"
                      :key="p.id"
                      :label="p.name"
                      :value="p.id">
                    </el-option>
                  </el-select>
                  <small class="text-muted">Se afișează primele {{ PRODUCTS_PER_PAGE }} produse; scrie pentru a căuta altele.</small>
                </div>

                <!-- TYPE 4: Testimonials -->
                <div class="form-group" v-if="form.section_type === 4">
                  <label>{{ typeLabel(4) }} (max {{ MAX_ITEMS }})</label>
                  <div v-for="(t, tIndex) in form.testimonials" :key="'t' + tIndex" class="item-box">
                    <div class="item-box__header">
                      <span>#{{ tIndex + 1 }}</span>
                      <a class="btn btn-danger btn-icon" @click="removeTestimonial(tIndex)" :title="$t('general.delete')">
                        <i class="ti-close"></i>
                      </a>
                    </div>
                    <div class="row">
                      <div class="col-md-4">
                        <label>{{ $t('marketingOffers.image') }}</label>
                        <el-upload
                          action=""
                          list-type="picture-card"
                          accept=".jpg,.jpeg,.png"
                          :file-list="t.imagelist"
                          :limit="1"
                          :auto-upload="false"
                          :on-change="(file, fileList) => handleTestimonialImg(tIndex, file, fileList)"
                          :on-remove="(file, fileList) => handleTestimonialRemove(tIndex, file, fileList)"
                          :on-exceed="handleExceed">
                          <img src="/static/img/icons/icon-plus-primary.png">
                        </el-upload>
                      </div>
                      <div class="col-md-8">
                        <div class="row">
                          <div class="col-md-6">
                            <div class="form-group">
                              <label>Locația</label>
                              <input type="text" class="form-control" v-model="t.title" />
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="form-group">
                              <label>Autor</label>
                              <input type="text" class="form-control" v-model="t.author" />
                            </div>
                          </div>
                        </div>
                        <div class="form-group">
                          <label>Notă <small class="text-muted">(0 - 5, ex: 4.8)</small></label>
                          <input type="number" class="form-control" v-model="t.rating"
                                 min="0" max="5" step="0.1" placeholder="ex: 4.8" />
                        </div>
                        <div class="form-group">
                          <label>Review</label>
                          <wysiwyg v-model="t.description" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-info btn-icon add mar-tp-10"
                          v-if="form.testimonials.length < MAX_ITEMS" @click="addTestimonial">
                    <img src="/static/img/icons/add.png">
                    {{ $t('general.add') }}
                  </button>
                </div>

                <!-- TYPE 5: Q&A -->
                <div class="form-group" v-if="form.section_type === 5">
                  <label>{{ typeLabel(5) }} (max {{ MAX_ITEMS }})</label>
                  <div v-for="(q, qIndex) in form.qas" :key="'q' + qIndex" class="item-box">
                    <div class="item-box__header">
                      <span>#{{ qIndex + 1 }}</span>
                      <a class="btn btn-danger btn-icon" @click="removeQa(qIndex)" :title="$t('general.delete')">
                        <i class="ti-close"></i>
                      </a>
                    </div>
                    <div class="form-group">
                      <label>{{ questionLabel }} <span class="text-danger">*</span></label>
                      <input type="text" class="form-control" v-model="q.question" />
                    </div>
                    <div class="form-group">
                      <label>{{ answerLabel }} <span class="text-danger">*</span></label>
                      <wysiwyg v-model="q.answer" />
                    </div>
                  </div>
                  <button type="button" class="btn btn-info btn-icon add mar-tp-10"
                          v-if="form.qas.length < MAX_ITEMS" @click="addQa">
                    <img src="/static/img/icons/add.png">
                    {{ $t('general.add') }}
                  </button>
                </div>

                <!-- TYPE 6: Newsletter -->
                <div class="form-group" v-if="form.section_type === 6">
                  <label>{{ $t('marketingOffers.image') }}</label>
                  <el-upload
                    action=""
                    list-type="picture-card"
                    accept=".jpg,.jpeg,.png"
                    :file-list="form.imagelist"
                    :limit="1"
                    :auto-upload="false"
                    :on-change="handleImagesChange"
                    :on-remove="handleImagesRemove"
                    :on-exceed="handleExceed">
                    <img src="/static/img/icons/icon-plus-primary.png">
                  </el-upload>
                  <p class="text-muted mar-tp-10">
                    Emailurile completate de clienți vor apărea la export.
                  </p>
                </div>

              </div>
            </div>
            <div class="sectionModal__footer border-top" style="display: flex; align-items: center; justify-content: space-between;">
              <button type="button" class="btn btn-default mar-rt-10" @click="closeModal">
                {{ $t('general.cancel') }}
              </button>
              <button type="button" class="btn btn-success btn-icon save" :disabled="saving" @click="save" :title="$t('general.save')">
                <img src="/static/img/icons/save.png">
                {{ $t('general.save') }}
              </button>
            </div>
          </vmodal>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'
  import { Upload, Select, Option } from 'element-ui'
  import draggable from 'vuedraggable'
  import vmodal from 'vue-js-modal'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'

  Vue.use(Upload)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(vmodal, { componentName: 'vmodal' })

  const MAX_ITEMS = 30
  const MIN_IMAGES = 5
  const PRODUCTS_PER_PAGE = 20
  const TITLE_MAX = 200
  const MAX_IMAGE_MB = 5

  export default {
    components: {
      draggable,
      PSwitch
    },
    data () {
      return {
        MAX_ITEMS,
        MIN_IMAGES,
        PRODUCTS_PER_PAGE,
        TITLE_MAX,
        loading: true,
        saving: false,
        exporting: false,
        productLoading: false,
        productsBrowsed: false,
        sections: [],
        products: [],
        form: this.emptyForm()
      }
    },
    computed: {
      companyID () {
        return this.$session.get('selectedCompany')
      },
      token () {
        return this.$session.get('token')
      },
      sectionTypes () {
        return [
          { value: 1, label: this.typeLabel(1) },
          { value: 2, label: this.typeLabel(2) },
          { value: 3, label: this.typeLabel(3) },
          { value: 4, label: this.typeLabel(4) },
          { value: 5, label: this.typeLabel(5) },
          { value: 6, label: this.typeLabel(6) }
        ]
      },
      hasNewsletter () {
        return this.sections.some(s => s.section_type === 6)
      },
      availableTypes () {
        // The newsletter section can only be added once per company.
        return this.hasNewsletter
          ? this.sectionTypes.filter(t => t.value !== 6)
          : this.sectionTypes
      },
      typeColumnLabel () {
        return 'Tip secțiune'
      },
      contentColumnLabel () {
        return 'Conținut'
      },
      questionLabel () {
        return 'Întrebare'
      },
      answerLabel () {
        return 'Răspuns'
      }
    },
    created () {
      this.initPage()
    },
    methods: {
      emptyForm () {
        return {
          id: null,
          section_type: 1,
          title: '',
          active: 1,
          description: '',
          image_fit: 'cover',
          imagelist: [],
          selectedProducts: [],
          testimonials: [],
          qas: []
        }
      },
      typeLabel (type) {
        const map = {
          1: 'Poze',
          2: 'Hero',
          3: 'Produse',
          4: 'Testimoniale',
          5: 'Q&A',
          6: 'Newsletter'
        }
        return map[type] || type
      },
      contentSummary (item) {
        switch (item.section_type) {
          case 1: return (item.images ? item.images.length : 0) + ' poze'
          case 2: return (item.images ? item.images.length : 0) + ' poze'
          case 3: return (item.products ? item.products.length : 0) + ' produse'
          case 4: return (item.testimonials ? item.testimonials.length : 0) + ' testimoniale'
          case 5: return (item.qas ? item.qas.length : 0) + ' întrebări'
          case 6: return 'Newsletter'
          default: return '-'
        }
      },
      baseData (extra) {
        return Object.assign({
          token: this.token,
          company_id: this.companyID
        }, extra || {})
      },
      initPage () {
        this.loading = true
        this.getSections()
      },
      getSections () {
        this.apiRequest('/homepage-sections', 'GET', this.baseData(), (response) => {
          if (response.status === true) {
            this.sections = response.data
          }
          this.$nextTick(() => { this.loading = false })
        })
      },
      /**
       * Open handler for the products dropdown: on first open, load the first
       * page of products so the user immediately sees options instead of an
       * empty list (they wouldn't otherwise know they have to start typing).
       */
      onProductDropdownVisible (visible) {
        if (visible && !this.productsBrowsed) {
          this.searchProducts('')
        }
      },
      /**
       * Remote product search for the products picker. The old picker loaded
       * the whole catalog up front, which froze the UI for large catalogs.
       * The API returns at most PRODUCTS_PER_PAGE matches per query; an empty
       * term returns the first page.
       */
      searchProducts (query) {
        const term = (query || '').trim()
        const params = term ? { q: term } : {}
        this.productLoading = true
        this.apiRequest('/homepage-sections/products/search', 'GET', this.baseData(params), (response) => {
          this.productLoading = false
          if (response.status === true) {
            this.products = this.mergeProducts(response.data)
            this.productsBrowsed = true
          }
        })
      },
      /**
       * Merge new results into the options list without dropping already
       * selected options (element-ui needs the selected options present to
       * keep showing their labels).
       */
      mergeProducts (incoming) {
        const selected = this.form.selectedProducts || []
        const kept = this.products.filter(p => selected.indexOf(p.id) !== -1)
        const byId = {}
        kept.concat(incoming || []).forEach(p => { byId[p.id] = p })
        return Object.keys(byId).map(id => byId[id])
      },
      /* ---------------- Modal open / close ---------------- */
      openCreate () {
        this.form = this.emptyForm()
        this.products = []
        this.$modal.show('sectionModal')
      },
      openEdit (item) {
        // Fetch fresh data with all children.
        this.apiRequest('/homepage-sections/' + item.id, 'GET', this.baseData(), (response) => {
          if (response.status === true) {
            this.form = this.mapToForm(response.data)
            this.$modal.show('sectionModal')
          } else {
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        })
      },
      closeModal () {
        this.$modal.hide('sectionModal')
      },
      onModalOpened () {
        // Each time the modal opens, allow the products dropdown to re-load its
        // first page on the next open.
        this.productsBrowsed = false
        // `.main-panel` has a transform, which makes it the containing block for
        // this modal's `position: fixed` container: the modal then scrolls with
        // the page and slides under the fixed sidebar. Re-parenting it to <body>
        // restores real viewport-fixed positioning. Only this modal is moved.
        const el = document.querySelector('.sectionModal.vm--container')
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el)
        }
      },
      mapToForm (data) {
        const form = this.emptyForm()
        form.id = data.id
        form.section_type = data.section_type
        form.title = data.title
        form.active = data.active ? 1 : 0
        form.description = data.description || ''
        form.image_fit = data.image_fit || 'cover'

        if (data.images) {
          form.imagelist = data.images.map(i => ({
            url: i.image_url,
            name: i.filename,
            filename: i.filename
          }))
        }
        if (data.products) {
          form.selectedProducts = data.products.map(p => p.product_id)
          // Seed the picker options with the already-selected products so
          // their tags render (the remote search won't have them otherwise).
          this.products = data.products
            .filter(p => p.product)
            .map(p => ({ id: p.product_id, name: p.product.name }))
        }
        if (data.testimonials) {
          form.testimonials = data.testimonials.map(t => ({
            filename: t.filename,
            imagelist: t.filename ? [{ url: t.image_url, name: t.filename, filename: t.filename }] : [],
            title: t.title || '',
            author: t.author || '',
            rating: (t.rating !== null && t.rating !== undefined) ? t.rating : '',
            description: t.description || ''
          }))
        }
        if (data.qas) {
          form.qas = data.qas.map(q => ({
            question: q.question || '',
            answer: q.answer || ''
          }))
        }
        return form
      },
      /* ---------------- Image handlers ---------------- */
      handleImagesChange (file, fileList) {
        if (this.isOversized(file)) {
          this.warnOversized()
        }
        // Rebuild the list from what el-upload holds, dropping every oversized
        // entry. Merely skipping the assignment is not enough: the rejected
        // file stays in el-upload's internal list and would be pulled back in
        // by the next file's change event (multi-select fires one per file).
        this.form.imagelist = fileList.filter(f => !this.isOversized(f))
      },
      handleImagesRemove (file, fileList) {
        this.form.imagelist = fileList.filter(f => !this.isOversized(f))
      },
      handleTestimonialImg (index, file, fileList) {
        if (this.isOversized(file)) {
          this.warnOversized()
        }
        const valid = fileList.filter(f => !this.isOversized(f))
        this.form.testimonials[index].imagelist = valid.slice(-1)
      },
      handleTestimonialRemove (index, file, fileList) {
        this.form.testimonials[index].imagelist = []
      },
      handleExceed () {
        swal('', 'Ai atins numărul maxim de fișiere.', 'warning').catch(() => {})
      },
      /**
       * Images already stored on the server carry no `size` (only a url), so
       * they must never be treated as oversized.
       */
      isOversized (file) {
        return !!(file && file.size && (file.size / 1024 / 1024) >= MAX_IMAGE_MB)
      },
      warnOversized () {
        swal({
          toast: true,
          type: 'warning',
          text: this.$t('validations.pictureSizeExceed'),
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        }).catch(() => {})
      },
      /* ---------------- Repeatable rows ---------------- */
      addTestimonial () {
        this.form.testimonials.push({ filename: null, imagelist: [], title: '', author: '', rating: '', description: '' })
      },
      removeTestimonial (index) {
        this.form.testimonials.splice(index, 1)
      },
      /**
       * A WYSIWYG field is rarely an empty string when the user clears it: it
       * usually keeps markup like <p></p>, <br> or &nbsp;. Strip tags and
       * entities to decide whether there is any real content.
       */
      isEmptyHtml (value) {
        if (!value) {
          return true
        }

        const text = String(value)
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, '')

        return text.length === 0
      },
      addQa () {
        this.form.qas.push({ question: '', answer: '' })
      },
      removeQa (index) {
        this.form.qas.splice(index, 1)
      },
      /* ---------------- Payload builders ---------------- */
      async collectImages () {
        const arr = []
        for (const f of this.form.imagelist) {
          if (f.raw) {
            arr.push(await this.getBase64(f.raw))
          } else if (f.filename) {
            arr.push(f.filename)
          }
        }
        return arr
      },
      async collectTestimonials () {
        const arr = []
        for (const t of this.form.testimonials) {
          let image = null
          if (t.imagelist && t.imagelist[0]) {
            if (t.imagelist[0].raw) {
              image = await this.getBase64(t.imagelist[0].raw)
            } else if (t.filename) {
              image = t.filename
            }
          } else if (t.filename) {
            image = t.filename
          }
          arr.push({
            image: image,
            title: t.title,
            author: t.author,
            rating: (t.rating === '' || t.rating === null || t.rating === undefined) ? null : t.rating,
            description: t.description
          })
        }
        return arr
      },
      /* ---------------- Save ---------------- */
      async save () {
        if (!this.form.title) {
          swal('', 'Titlul este obligatoriu.', 'warning').catch(() => {})
          return
        }

        if (this.form.title.length > TITLE_MAX) {
          swal('', 'Titlul poate avea maxim ' + TITLE_MAX + ' caractere.', 'warning').catch(() => {})
          return
        }

        // Images: at least MIN_IMAGES pictures are required.
        if (this.form.section_type === 1 && this.form.imagelist.length < MIN_IMAGES) {
          swal('', 'Adaugă cel puțin ' + MIN_IMAGES + ' poze.', 'warning').catch(() => {})
          return
        }

        // Testimonials: the rating, when filled, must be a number 0 - 5.
        if (this.form.section_type === 4) {
          const badRating = this.form.testimonials.findIndex(t => {
            if (t.rating === '' || t.rating === null || t.rating === undefined) {
              return false
            }
            const n = Number(t.rating)
            return isNaN(n) || n < 0 || n > 5
          })

          if (badRating !== -1) {
            swal('', 'Nota trebuie să fie un număr între 0 și 5 (verifică #' + (badRating + 1) + ').', 'warning').catch(() => {})
            return
          }
        }

        // Q&A: both the question and the answer are mandatory on every row.
        if (this.form.section_type === 5) {
          if (!this.form.qas.length) {
            swal('', 'Adaugă cel puțin o întrebare.', 'warning').catch(() => {})
            return
          }

          const incomplete = this.form.qas.findIndex(
            q => !q.question || !q.question.trim() || this.isEmptyHtml(q.answer)
          )

          if (incomplete !== -1) {
            swal('', 'Întrebarea și răspunsul sunt obligatorii (verifică #' + (incomplete + 1) + ').', 'warning').catch(() => {})
            return
          }
        }

        this.saving = true

        const data = this.baseData({
          section_type: this.form.section_type,
          title: this.form.title,
          description: this.form.description,
          image_fit: this.form.image_fit,
          active: this.form.active ? 1 : 0
        })

        try {
          switch (this.form.section_type) {
            case 1:
              data.images = await this.collectImages()
              break
            case 2:
              data.images = await this.collectImages()
              break
            case 3:
              data.products = this.form.selectedProducts
              break
            case 4:
              data.testimonials = await this.collectTestimonials()
              break
            case 5:
              data.qas = this.form.qas
              break
            case 6:
              data.images = await this.collectImages()
              break
          }
        } catch (e) {
          this.saving = false
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          return
        }

        const isUpdate = !!this.form.id
        const url = isUpdate ? '/homepage-sections/' + this.form.id : '/homepage-sections'
        const method = isUpdate ? 'PUT' : 'POST'

        this.apiRequest(url, method, data, (response) => {
          this.saving = false
          if (response.status === true) {
            this.closeModal()
            this.getSections()
            swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          } else {
            const msg = response.errors ? Object.values(response.errors)[0] : this.$t('validations.wentWrong')
            swal('', msg, 'error').catch(() => {})
          }
        })
      },
      /* ---------------- Active toggle ---------------- */
      handleActive (row) {
        const actionText = row.active ? this.$t('general.activate') : this.$t('general.deactivate')
        swal({
          text: this.$t('validations.areYouSureYouWantTo') + ` ${actionText} ${row.title}?`,
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('general.yes')
        }).then((result) => {
          if (result) {
            const data = this.baseData({ active: row.active ? 1 : 0 })
            this.apiRequest('/homepage-sections/' + row.id, 'PUT', data, (response) => {
              if (response.status !== true) {
                row.active = !row.active
                swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
              }
            })
          } else {
            row.active = !row.active
          }
        }).catch(() => {
          row.active = !row.active
        })
      },
      /* ---------------- Newsletter export ---------------- */
      exportNewsletter () {
        this.exporting = true
        this.apiRequest('/homepage-sections/newsletter/export', 'GET', this.baseData(), (response) => {
          this.exporting = false
          if (response.status === true && response.data && response.data.file) {
            const link = document.createElement('a')
            link.href = response.data.file
            link.setAttribute('download', response.data.file_name)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          } else {
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        })
      },
      /* ---------------- Reorder ---------------- */
      onReorder () {
        const order = this.sections.map(s => s.id)
        const data = this.baseData({ order: order })
        this.apiRequest('/homepage-sections/reorder', 'POST', data, (response) => {
          if (response.status !== true) {
            swal({
              title: '',
              text: this.$t('validations.dataCouldNotBeSaved'),
              type: 'error',
              showConfirmButton: false,
              timer: 1500
            }).catch(() => {})
          }
        })
      },
      /* ---------------- Delete ---------------- */
      handleDelete (index, row, event) {
        const self = this
        if (event) event.stopPropagation()
        swal({
          text: self.$t('marketingCampaigns.areYouSureDelete') + ` '${row.title}'?`,
          type: 'question',
          showCancelButton: true,
          confirmButtonText: self.$t('general.yes')
        }).then((result) => {
          if (result) {
            this.apiRequest('/homepage-sections/' + row.id, 'DELETE', this.baseData(), (response) => {
              if (response.status === true) {
                self.sections.splice(index, 1)
                swal('', self.$t('general.success') + '!', 'success').catch(() => {})
              } else {
                swal('', self.$t('validations.wentWrong'), 'error').catch(() => {})
              }
            })
          }
        }).catch(() => {})
      }
    }
  }
</script>

<style scoped>
  .border-bottom {
    border-bottom: 1px solid #ccc;
  }
  .border-top {
    border-top: 1px solid #ccc;
  }
  .header {
    padding: 15px;
  }
  /*
   * vue-js-modal v2 box (.vm--modal). Cap it to the viewport and lay it out as
   * a column so the header/footer stay put and only the body scrolls.
   * Scoped to THIS modal via the .sectionModal class, so no other modal in the
   * project is affected.
   */
  /*
   * NOTE: `.main-panel` has a transform, which makes it the containing block
   * for the modal's `position: fixed` container. vue-js-modal still computes
   * its inline width/left from the viewport, so the box ends up wider than its
   * container and spills off screen. We therefore size the modal against its
   * own container instead of the viewport, overriding those inline styles.
   */
  .sectionModal >>> .vm--modal {
    top: 0 !important;
    left: 0 !important;
    width: 96% !important;
    max-width: 1300px !important;
    max-height: 88vh !important;
    margin: 24px auto !important;
    display: flex;
    flex-direction: column;
  }

  .content-group {
    margin: 10px 15px;
    flex: 1 1 auto;
    /* Keeps the popup tall enough that the product dropdown (capped at 200px
       below) opens inside it instead of spilling past the bottom edge. */
    min-height: 460px;
    overflow-y: auto;
    padding-bottom: 24px;
  }

  /* Short viewports: let the body shrink so the modal still fits on screen. */
  @media (max-height: 800px) {
    .content-group {
      min-height: 240px;
    }
  }
  .sectionModal__footer {
    padding: 15px;
    text-align: right;
  }
  .mb-10 {
    margin-bottom: 10px;
  }
  .mar-tp-10 {
    margin-top: 10px;
  }
  .mar-tp-20 {
    margin-top: 20px;
  }
  .item-box {
    border: 1px solid #E8ECFF;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
    background: #FBFCFF;
  }
  .item-box__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-weight: 600;
  }

  /* Title cell: cap the width and clamp long titles to 2 lines with an
     ellipsis. Full text stays available via the title tooltip. */
  .hs-title-cell {
    /* min-width keeps normal titles readable (the column would otherwise be
       squeezed to ~78px by the percentage th widths); max-width caps the long
       ones, which then clamp to 2 lines. */
    min-width: 200px;
    max-width: 500px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }

  /* Sections table: scroll sideways instead of squashing on narrow screens. */
  .hs-table-wrap {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Space after the whole card, on the main page scroll, so the floating
     "new orders" widget (fixed bottom-right) has room of its own. */
  .hs-sections-page {
    padding-bottom: 150px;
  }
  .hs-table-wrap > table {
    min-width: 900px;
  }

  /* Top bar buttons wrap nicely on small screens. */
  @media (max-width: 600px) {
    .card-content .pull-right {
      float: none !important;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .card-content .pull-right .btn {
      flex: 1 1 auto;
      margin-right: 0;
    }
  }

  /*
   * Multiple product select: let the field grow in height as tags wrap.
   * Scoped to THIS select only (.hs-product-select + >>>), so no other
   * el-select in the project is affected. This works around the global
   * `.el-input__inner { height: 46px !important }` rule that otherwise
   * clips the wrapped tags.
   */
  .hs-product-select {
    display: block;
    width: 100%;
    min-height: 46px;
    border: 1px solid #E6EFFB;
    background-color: #E6EFFB;
    border-radius: 6px;
    box-sizing: border-box;
    overflow: hidden;
  }
  /* Tags flow inside the field instead of overlaying a fixed-height input. */
  .hs-product-select >>> .el-select__tags {
    position: relative;
    top: auto;
    -webkit-transform: none;
    transform: none;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0;
    padding: 6px 8px 2px 8px;
    box-sizing: border-box;
  }
  /* The real input becomes a borderless, transparent line inside the field. */
  .hs-product-select >>> .el-input__inner {
    height: 34px !important;
    min-height: 34px !important;
    line-height: 34px !important;
    border: none !important;
    background-color: transparent !important;
  }

  /* Responsive: tighten the popup on smaller screens. Scoped to this modal. */
  @media (max-width: 991px) {
    .content-group {
      margin: 10px;
    }
    .header,
    .sectionModal__footer {
      padding: 10px;
    }
  }

  @media (max-width: 600px) {
    .content-group {
      min-height: 240px;
    }
    .item-box {
      padding: 10px;
    }
    .sectionModal__footer .btn {
      display: block;
      width: 100%;
      margin: 0 0 8px 0;
    }
  }
</style>

<!--
  The product dropdown is rendered into <body> by element-ui, so scoped styles
  cannot reach it. This block is unscoped on purpose but keyed on a unique
  popper-class, so it only ever affects this one select's dropdown.
-->
<style>
  .hs-product-dropdown .el-select-dropdown__wrap {
    max-height: 200px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Online/HomepageSections.vue